/**
 * offer.ts — the Offert-Posteingang + IDP contract (P2 Placement Slice 10, Ausschreibungs-Screen
 * Tab 4a, Prozessschritte 9 + 10; Detailkonzept Ausschreibung V3.0 Ch 6.5 / 8.5 / 8.6 / 9 / 10 / 11,
 * P2 §7 step 9 + 10, §8 E9 + E11, §10.2, Ü5, UC8 + UC9).
 *
 * The SINGLE SOURCE OF TRUTH for:
 *   - the offer status vocabulary + the 10.2 status machine (eingegangen -> normalisiert -> ...),
 *   - the IDP pipeline-state vocabulary (Ch 6.5) + the source / match-art / Deckung-status / Ansicht
 *     vocabularies,
 *   - the confidence-routing constant + the extraction retry/schema constants,
 *   - the pure normalisation (normalizeOffer -> OfferNormalizedV1, routeOfferExtraction, the E11
 *     merge) shared so api + web + tests agree,
 *   - the wire shapes (list / upload / the action bodies) + the pure counters.
 *
 * Pure module — no I/O, no formatters, no Date-TZ parse.
 *
 * [Source: docs/product/p2-slices/slice-10-offer-intake-design.md §3]
 * [Source: packages/shared/src/qna.ts] (triad + Sperrgrund + wire + counter idioms)
 * [Source: packages/shared/src/submission.ts] (status machine idiom)
 */

import type { PlacementStatus } from './placement';
import type { SubmissionStatus } from './submission';
import type { Step2AnswerItem } from './placement-snapshot';
import type { SollPflichtStatus } from './police-risiko';

// ── Vocabularies (triad idiom) ───────────────────────────────────────────────

/** The §10.2 lifecycle status of one offer (Slice 10 writes the first two; 11/12 the rest). */
export const OFFER_STATUS = {
  EINGEGANGEN: 'eingegangen',
  NORMALISIERT: 'normalisiert',
  IM_VERGLEICH: 'im_vergleich',
  GEWAEHLT: 'gewaehlt',
  NICHT_GEWAEHLT: 'nicht_gewaehlt',
} as const;
export type OfferStatus = (typeof OFFER_STATUS)[keyof typeof OFFER_STATUS];
export const OFFER_STATUS_VALUES: readonly OfferStatus[] = [
  OFFER_STATUS.EINGEGANGEN,
  OFFER_STATUS.NORMALISIERT,
  OFFER_STATUS.IM_VERGLEICH,
  OFFER_STATUS.GEWAEHLT,
  OFFER_STATUS.NICHT_GEWAEHLT,
];
export const OFFER_STATUS_LABELS: Record<OfferStatus, string> = {
  [OFFER_STATUS.EINGEGANGEN]: 'Eingegangen',
  [OFFER_STATUS.NORMALISIERT]: 'Normalisiert',
  [OFFER_STATUS.IM_VERGLEICH]: 'Im Vergleich',
  [OFFER_STATUS.GEWAEHLT]: 'Gewaehlt',
  [OFFER_STATUS.NICHT_GEWAEHLT]: 'Nicht gewaehlt',
};

/** The Ch 6.5 IDP pipeline state of one offer. */
export const OFFER_IDP_STATUS = {
  AUSSTEHEND: 'ausstehend',
  KLASSIFIZIERT: 'klassifiziert',
  NORMALISIERT: 'normalisiert',
  MANUELL_ZU_PRUEFEN: 'manuell_zu_pruefen',
  KEINE_OFFERTE: 'keine_offerte',
  FEHLGESCHLAGEN: 'fehlgeschlagen',
} as const;
export type OfferIdpStatus = (typeof OFFER_IDP_STATUS)[keyof typeof OFFER_IDP_STATUS];
export const OFFER_IDP_STATUS_VALUES: readonly OfferIdpStatus[] = [
  OFFER_IDP_STATUS.AUSSTEHEND,
  OFFER_IDP_STATUS.KLASSIFIZIERT,
  OFFER_IDP_STATUS.NORMALISIERT,
  OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN,
  OFFER_IDP_STATUS.KEINE_OFFERTE,
  OFFER_IDP_STATUS.FEHLGESCHLAGEN,
];
export const OFFER_IDP_STATUS_LABELS: Record<OfferIdpStatus, string> = {
  [OFFER_IDP_STATUS.AUSSTEHEND]: 'Extraktion ausstehend',
  [OFFER_IDP_STATUS.KLASSIFIZIERT]: 'Klassifiziert',
  [OFFER_IDP_STATUS.NORMALISIERT]: 'Normalisiert',
  [OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN]: 'Manuell zu pruefen',
  [OFFER_IDP_STATUS.KEINE_OFFERTE]: 'Keine Offerte',
  [OFFER_IDP_STATUS.FEHLGESCHLAGEN]: 'Extraktion fehlgeschlagen',
};

/** Where an offer file entered the system (ecohub reserved, no producer in Slice 10). */
export const OFFER_SOURCE = {
  POSTEINGANG: 'posteingang',
  UPLOAD: 'upload',
  ECOHUB: 'ecohub',
} as const;
export type OfferSource = (typeof OFFER_SOURCE)[keyof typeof OFFER_SOURCE];
export const OFFER_SOURCE_VALUES: readonly OfferSource[] = [
  OFFER_SOURCE.POSTEINGANG,
  OFFER_SOURCE.UPLOAD,
  OFFER_SOURCE.ECOHUB,
];
export const OFFER_SOURCE_LABELS: Record<OfferSource, string> = {
  [OFFER_SOURCE.POSTEINGANG]: 'Posteingang',
  [OFFER_SOURCE.UPLOAD]: 'Manueller Upload',
  [OFFER_SOURCE.ECOHUB]: 'EcoHub',
};

/** How the submission was found for an offer. */
export const OFFER_MATCH_ART = {
  THREAD: 'thread',
  BETREFF: 'betreff',
  MANUELL: 'manuell',
} as const;
export type OfferMatchArt = (typeof OFFER_MATCH_ART)[keyof typeof OFFER_MATCH_ART];
export const OFFER_MATCH_ART_VALUES: readonly OfferMatchArt[] = [
  OFFER_MATCH_ART.THREAD,
  OFFER_MATCH_ART.BETREFF,
  OFFER_MATCH_ART.MANUELL,
];
export const OFFER_MATCH_ART_LABELS: Record<OfferMatchArt, string> = {
  [OFFER_MATCH_ART.THREAD]: 'E-Mail-Verlauf',
  [OFFER_MATCH_ART.BETREFF]: 'Submissions-Nummer im Betreff',
  [OFFER_MATCH_ART.MANUELL]: 'Manuell zugeordnet',
};

/** The status of a single coverage element in the normalised offer. */
export const OFFER_DECKUNG_STATUS = {
  EINGESCHLOSSEN: 'eingeschlossen',
  AUSGESCHLOSSEN: 'ausgeschlossen',
  UNBEKANNT: 'unbekannt',
} as const;
export type OfferDeckungStatus = (typeof OFFER_DECKUNG_STATUS)[keyof typeof OFFER_DECKUNG_STATUS];
export const OFFER_DECKUNG_STATUS_VALUES: readonly OfferDeckungStatus[] = [
  OFFER_DECKUNG_STATUS.EINGESCHLOSSEN,
  OFFER_DECKUNG_STATUS.AUSGESCHLOSSEN,
  OFFER_DECKUNG_STATUS.UNBEKANNT,
];
export const OFFER_DECKUNG_STATUS_LABELS: Record<OfferDeckungStatus, string> = {
  [OFFER_DECKUNG_STATUS.EINGESCHLOSSEN]: 'eingeschlossen',
  [OFFER_DECKUNG_STATUS.AUSGESCHLOSSEN]: 'ausgeschlossen',
  [OFFER_DECKUNG_STATUS.UNBEKANNT]: 'nicht erkannt',
};

/** The Tab-4a view toggle. */
export const OFFER_ANSICHT = {
  KACHEL: 'kachel',
  TABELLE: 'tabelle',
} as const;
export type OfferAnsicht = (typeof OFFER_ANSICHT)[keyof typeof OFFER_ANSICHT];
export const OFFER_ANSICHT_VALUES: readonly OfferAnsicht[] = [
  OFFER_ANSICHT.KACHEL,
  OFFER_ANSICHT.TABELLE,
];
export const OFFER_ANSICHT_LABELS: Record<OfferAnsicht, string> = {
  [OFFER_ANSICHT.KACHEL]: 'Kacheln',
  [OFFER_ANSICHT.TABELLE]: 'Tabelle',
};

// ── Status machine (§10.2) ────────────────────────────────────────────────────

export const OFFER_STATUS_TRANSITIONS: Record<OfferStatus, readonly OfferStatus[]> = {
  eingegangen: ['normalisiert'],
  normalisiert: ['im_vergleich'],
  im_vergleich: ['gewaehlt', 'nicht_gewaehlt'],
  gewaehlt: [],
  nicht_gewaehlt: [],
};

/** Is `to` a legal next offer status from `from`? */
export function canTransitionOffer(from: OfferStatus, to: OfferStatus): boolean {
  return OFFER_STATUS_TRANSITIONS[from].includes(to);
}

/** The offer statuses in which zuordnen / re-extract / aktuell / verwerfen / normalisiert are allowed. */
export const OFFER_BEARBEITBARE_STATUS: readonly OfferStatus[] = ['eingegangen', 'normalisiert'];

/** The placement statuses in which an offer may be uploaded / captured (after Versand, before entschieden). */
export const OFFER_UPLOAD_ERLAUBTE_STATUS: readonly PlacementStatus[] = [
  'ausgeschrieben',
  'verglichen',
  'empfohlen',
];

/** The submission statuses an offer may be assigned to (a late offer trumps a decline, Q64). */
export const OFFER_ZUORDENBARE_SUBMISSION_STATUS: readonly SubmissionStatus[] = [
  'versandt',
  'erinnert',
  'eingegangen',
  'nicht_offeriert',
];

/** Is an offer in an editable status? (The service also requires not verworfen.) */
export function isOfferBearbeitbar(status: OfferStatus): boolean {
  return OFFER_BEARBEITBARE_STATUS.includes(status);
}

// ── Routing / extraction constants ──────────────────────────────────────────────

/** Confidence threshold for automatic `normalisiert` (Q81; the Slice-6 auto-threshold lineage). */
export const OFFER_NORMALISIERT_MIN_CONFIDENCE = 0.85;
/** Max extractor attempts before a terminal failure task. */
export const OFFER_EXTRACT_MAX_ATTEMPTS = 3;
/** The pinned OfferNormalizedV1 schema version. */
export const OFFER_NORMALIZED_SCHEMA_VERSION = 1 as const;
/** Max upload size (20 MB, the P1 upload idiom). */
export const OFFER_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
/** The only accepted offer upload MIME. */
export const OFFER_PDF_MIME = 'application/pdf';

// ── Extractor result wire (api OfferExtractResult minus raw_text_excerpt) ────────

/** One coverage element the extractor reported. */
export interface OfferExtractDeckungWire {
  label: string;
  eingeschlossen: boolean;
  deckungssumme?: string;
  selbstbehalt?: string;
  hinweis?: string;
  rule_key_hint?: string;
}

/**
 * The extractor result as it travels to the normaliser (api `OfferExtractResult` minus
 * `raw_text_excerpt`, which is PII and never persisted/logged). Declared here so api + web + tests
 * share one shape.
 */
export interface OfferExtractResultWire {
  confidence: number;
  source: 'fixture' | 'phoeniqs';
  provider_document_id?: string;
  is_offer: boolean;
  carrier_name?: string;
  offer_number?: string;
  offer_date?: string;
  valid_until?: string;
  currency?: string;
  premium_annual?: string;
  premium_net?: string;
  premium_gross?: string;
  sum_insured?: string;
  deductible?: string;
  contract_term_months?: number;
  begin_date?: string;
  deckungen: OfferExtractDeckungWire[];
  ausschluesse: string[];
  warnings: string[];
}

/** The Soll answer fields the normaliser reads (a Pick of the Step-2 answer). */
export type OfferSollAnswer = Pick<
  Step2AnswerItem,
  'rule_key' | 'label' | 'pflicht_status' | 'effective_target_value' | 'selbstbehalt_override'
>;

// ── Normalised offer (OfferNormalizedV1, D5) ─────────────────────────────────────

export interface OfferNormalizedDeckung {
  /** The Soll rule it satisfies (null = Zusatzdeckung outside the Soll-Katalog). */
  rule_key: string | null;
  label: string;
  pflicht_status: SollPflichtStatus | null;
  status: OfferDeckungStatus;
  deckungssumme: number | null;
  selbstbehalt: number | null;
  hinweis: string | null;
  /** Copied from the Soll answer (effective values) for the Slice-11 compare. */
  soll_zielsumme: number | null;
  soll_selbstbehalt: number | null;
  match: 'rule_key' | 'label' | 'keine';
}

export interface OfferNormalizedV1 {
  schema_version: 1;
  waehrung: string;
  praemie: { jahr: number | null; netto: number | null; brutto: number | null };
  versicherungssumme: number | null;
  selbstbehalt: number | null;
  vertragsdauer_monate: number | null;
  beginn: string | null;
  gueltig_bis: string | null;
  offert_nummer: string | null;
  versicherer_name: string | null;
  /** Every Soll rule of the placement first (answer order), then extras. */
  deckungen: OfferNormalizedDeckung[];
  ausschluesse: string[];
  soll_abdeckung: { total: number; gematcht: number; unbekannt: number };
  /** praemie.jahr AND versicherungssumme non-null. */
  kernwerte_vollstaendig: boolean;
  quelle: {
    extractor: 'fixture' | 'phoeniqs' | 'manuell';
    confidence: number;
    extracted_at: string;
    manuell_ergaenzt: boolean;
  };
}

// ── Pure normalisation (D5) ──────────────────────────────────────────────────────

/** lower-case, ae/oe/ue/e substitutions, non-alphanumerics removed. */
export function normalizeLabel(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/é/g, 'e')
    .replace(/è/g, 'e')
    .replace(/ê/g, 'e')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Code-maintained synonym list (Q88). ONLY keys that exist in SOLL_DECKUNG_KATALOG — a spec asserts
 * every key resolves. The entries are already in normalizeLabel form (lower-case, no non-alnum).
 */
export const OFFER_DECKUNG_SYNONYME: Record<string, readonly string[]> = {
  betriebshaftpflicht_deckung: [
    'betriebshaftpflicht',
    'betriebsundprodukthaftpflicht',
    'haftpflicht',
  ],
};

/** Parse a decimal money string (2-dp) to a number; invalid / absent → null. */
function parseMoney(s: string | null | undefined): number | null {
  if (s === null || s === undefined) return null;
  const trimmed = s.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return n;
}

function computeSollAbdeckung(deckungen: readonly OfferNormalizedDeckung[]): {
  total: number;
  gematcht: number;
  unbekannt: number;
} {
  let total = 0;
  let unbekannt = 0;
  for (const d of deckungen) {
    if (d.rule_key === null) continue;
    total += 1;
    if (d.status === OFFER_DECKUNG_STATUS.UNBEKANNT) unbekannt += 1;
  }
  return { total, gematcht: total - unbekannt, unbekannt };
}

/**
 * Normalise an extractor result against the placement's Soll answers into the pinned
 * OfferNormalizedV1 shape. Pure; `now` is the ISO timestamp stamped into `quelle.extracted_at`.
 */
export function normalizeOffer(
  result: OfferExtractResultWire,
  soll: readonly OfferSollAnswer[],
  now: string,
): OfferNormalizedV1 {
  const praemieJahr = parseMoney(result.premium_annual) ?? parseMoney(result.premium_gross);
  const versicherungssumme = parseMoney(result.sum_insured);
  const consumed = new Set<number>();

  const deckungen: OfferNormalizedDeckung[] = [];
  for (const answer of soll) {
    const sollNorm = normalizeLabel(answer.label);
    const syn = OFFER_DECKUNG_SYNONYME[answer.rule_key] ?? [];
    let hitIdx = -1;
    let hitMatch: 'rule_key' | 'label' = 'label';
    // (1) rule_key_hint
    for (let i = 0; i < result.deckungen.length; i += 1) {
      if (consumed.has(i)) continue;
      if (result.deckungen[i]!.rule_key_hint === answer.rule_key) {
        hitIdx = i;
        hitMatch = 'rule_key';
        break;
      }
    }
    // (2) label / synonym
    if (hitIdx === -1) {
      for (let i = 0; i < result.deckungen.length; i += 1) {
        if (consumed.has(i)) continue;
        const extNorm = normalizeLabel(result.deckungen[i]!.label);
        if (extNorm === sollNorm || syn.includes(extNorm)) {
          hitIdx = i;
          hitMatch = 'label';
          break;
        }
      }
    }
    if (hitIdx === -1) {
      deckungen.push({
        rule_key: answer.rule_key,
        label: answer.label,
        pflicht_status: answer.pflicht_status,
        status: OFFER_DECKUNG_STATUS.UNBEKANNT,
        deckungssumme: null,
        selbstbehalt: null,
        hinweis: null,
        soll_zielsumme: answer.effective_target_value,
        soll_selbstbehalt: answer.selbstbehalt_override,
        match: 'keine',
      });
    } else {
      consumed.add(hitIdx);
      const ext = result.deckungen[hitIdx]!;
      deckungen.push({
        rule_key: answer.rule_key,
        label: answer.label,
        pflicht_status: answer.pflicht_status,
        status: ext.eingeschlossen
          ? OFFER_DECKUNG_STATUS.EINGESCHLOSSEN
          : OFFER_DECKUNG_STATUS.AUSGESCHLOSSEN,
        deckungssumme: parseMoney(ext.deckungssumme),
        selbstbehalt: parseMoney(ext.selbstbehalt),
        hinweis: ext.hinweis ?? null,
        soll_zielsumme: answer.effective_target_value,
        soll_selbstbehalt: answer.selbstbehalt_override,
        match: hitMatch,
      });
    }
  }
  // Appended extractor coverages with no Soll rule.
  for (let i = 0; i < result.deckungen.length; i += 1) {
    if (consumed.has(i)) continue;
    const ext = result.deckungen[i]!;
    deckungen.push({
      rule_key: null,
      label: ext.label,
      pflicht_status: null,
      status: ext.eingeschlossen
        ? OFFER_DECKUNG_STATUS.EINGESCHLOSSEN
        : OFFER_DECKUNG_STATUS.AUSGESCHLOSSEN,
      deckungssumme: parseMoney(ext.deckungssumme),
      selbstbehalt: parseMoney(ext.selbstbehalt),
      hinweis: ext.hinweis ?? null,
      soll_zielsumme: null,
      soll_selbstbehalt: null,
      match: 'keine',
    });
  }

  const kernwerteVollstaendig = praemieJahr !== null && versicherungssumme !== null;
  return {
    schema_version: OFFER_NORMALIZED_SCHEMA_VERSION,
    waehrung: result.currency ?? 'CHF',
    praemie: {
      jahr: praemieJahr,
      netto: parseMoney(result.premium_net),
      brutto: parseMoney(result.premium_gross),
    },
    versicherungssumme,
    selbstbehalt: parseMoney(result.deductible),
    vertragsdauer_monate: result.contract_term_months ?? null,
    beginn: result.begin_date ?? null,
    gueltig_bis: result.valid_until ?? null,
    offert_nummer: result.offer_number ?? null,
    versicherer_name: result.carrier_name ?? null,
    deckungen,
    ausschluesse: [...result.ausschluesse],
    soll_abdeckung: computeSollAbdeckung(deckungen),
    kernwerte_vollstaendig: kernwerteVollstaendig,
    quelle: {
      extractor: result.source,
      confidence: result.confidence,
      extracted_at: now,
      manuell_ergaenzt: false,
    },
  };
}

/** Route an extraction to its IDP status (Q81). */
export function routeOfferExtraction(
  result: OfferExtractResultWire,
  normalized: OfferNormalizedV1,
): 'normalisiert' | 'manuell_zu_pruefen' | 'keine_offerte' {
  if (!result.is_offer) return OFFER_IDP_STATUS.KEINE_OFFERTE;
  if (result.confidence >= OFFER_NORMALISIERT_MIN_CONFIDENCE && normalized.kernwerte_vollstaendig) {
    return OFFER_IDP_STATUS.NORMALISIERT;
  }
  return OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN;
}

/** The subset of the two Pflicht-Kernwerte still missing. */
export function fehlendeKernwerte(n: OfferNormalizedV1): string[] {
  const missing: string[] = [];
  if (n.praemie.jahr === null) missing.push('praemie_jahr');
  if (n.versicherungssumme === null) missing.push('versicherungssumme');
  return missing;
}

/** Merge the E11 completion fields over the stored JSON, re-deriving the derived fields (D5). */
export function mergeOfferNormalized(
  current: OfferNormalizedV1,
  patch: OfferNormalisiertBody,
  now: string,
): OfferNormalizedV1 {
  const pick = <T>(patchVal: T | undefined, cur: T): T => (patchVal === undefined ? cur : patchVal);
  const deckungen = current.deckungen.map((d) => ({ ...d }));
  if (patch.deckungen) {
    for (const pd of patch.deckungen) {
      const idx = deckungen.findIndex((d) => d.rule_key === pd.rule_key);
      if (idx === -1) continue;
      deckungen[idx] = {
        ...deckungen[idx]!,
        status: pd.status,
        deckungssumme: pd.deckungssumme === undefined ? deckungen[idx]!.deckungssumme : pd.deckungssumme,
        selbstbehalt: pd.selbstbehalt === undefined ? deckungen[idx]!.selbstbehalt : pd.selbstbehalt,
        hinweis: pd.hinweis === undefined ? deckungen[idx]!.hinweis : pd.hinweis,
      };
    }
  }
  const versicherungssumme = pick(patch.versicherungssumme, current.versicherungssumme);
  const praemieJahr = pick(patch.praemie_jahr, current.praemie.jahr);
  return {
    ...current,
    praemie: {
      jahr: praemieJahr,
      netto: pick(patch.praemie_netto, current.praemie.netto),
      brutto: pick(patch.praemie_brutto, current.praemie.brutto),
    },
    versicherungssumme,
    selbstbehalt: pick(patch.selbstbehalt, current.selbstbehalt),
    vertragsdauer_monate: pick(patch.vertragsdauer_monate, current.vertragsdauer_monate),
    beginn: pick(patch.beginn, current.beginn),
    gueltig_bis: pick(patch.gueltig_bis, current.gueltig_bis),
    offert_nummer: pick(patch.offert_nummer, current.offert_nummer),
    deckungen,
    ausschluesse: patch.ausschluesse === undefined ? current.ausschluesse : [...patch.ausschluesse],
    soll_abdeckung: computeSollAbdeckung(deckungen),
    kernwerte_vollstaendig: praemieJahr !== null && versicherungssumme !== null,
    quelle: {
      ...current.quelle,
      extractor: 'manuell',
      extracted_at: now,
      manuell_ergaenzt: true,
    },
  };
}

// ── Texts (ASCII hyphens only) ────────────────────────────────────────────────────

export const OFFER_KEINE_OFFERTEN_HINWEIS =
  'Noch keine Offerten - eingehende E-Mails mit PDF-Anhang zu einer versandten Offertanfrage erscheinen hier automatisch. Sie koennen Offerten auch manuell hochladen.';
export const OFFER_NICHT_ZUGEORDNET_LABEL = 'Nicht zugeordnet';
export const OFFER_NICHT_ZUGEORDNET_HINWEIS =
  'Diese Offerte ist noch keinem Versicherer zugeordnet - bitte zuordnen, damit sie im Vergleich beruecksichtigt wird.';
export const OFFER_AKTUELL_LABEL = 'Aktuelle Version';
export const OFFER_VERSION_LABEL_PREFIX = 'Version ';
export const OFFER_MANUELL_HINWEIS =
  'Die automatische Erkennung war nicht eindeutig - bitte die fehlenden Werte ergaenzen und bestaetigen.';
export const OFFER_KEINE_OFFERTE_HINWEIS =
  'Die Datei wurde nicht als Offerte erkannt (z. B. AVB oder Begleitschreiben).';
export const OFFER_VERWORFEN_LABEL = 'Verworfen';
export const OFFER_UPLOAD_NUR_PDF_HINWEIS = 'Nur PDF-Dateien bis 20 MB.';
export const OFFER_PLACEMENT_STATUS_HINWEIS =
  'Offerten koennen erst nach dem Versand der Offertanfragen erfasst werden.';
export const OFFER_THUMBNAIL_HINWEIS = 'Vorschau im Dokument-Viewer oeffnen';

// ── Events (Ch 11 verbatim + Slice-10 additions) ───────────────────────────────────

export const OFFER_EVENTS = {
  RECEIVED: 'submission.offer_received',
  NORMALIZED: 'placement.offer.normalized',
  MANUAL_REVIEW_REQUIRED: 'placement.offer.manual_review_required',
  ASSIGNED: 'placement.offer.assigned',
  VERSION_SUPERSEDED: 'placement.offer.version_superseded',
  EXTRACTION_FAILED: 'placement.offer.extraction_failed',
  DISCARDED: 'placement.offer.discarded',
} as const;
export type OfferEvent = (typeof OFFER_EVENTS)[keyof typeof OFFER_EVENTS];

// ── Wire types ──────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- the per-offer coverage row is the normalised shape
export interface OfferDeckungEintrag extends OfferNormalizedDeckung {}

export interface OfferEintrag {
  id: string;
  document_id: string;
  communication_id: string;
  source: OfferSource;
  submission_id: string | null;
  submission_number: string | null;
  carrier_uid: string | null;
  carrier_name: string | null;
  logo_url: string | null;
  status: OfferStatus;
  status_label: string;
  idp_status: OfferIdpStatus;
  idp_status_label: string;
  match_art: OfferMatchArt | null;
  version_no: number | null;
  is_current: boolean;
  abgeloest_at: string | null;
  filename: string;
  betreff: string | null;
  absender: string | null;
  received_at: string;
  thumbnail_ref: string | null;
  /** GET .../offerten/:id/datei (relative API path). */
  datei_url: string;
  extractor_driver: 'fixture' | 'phoeniqs' | 'manuell' | null;
  extractor_confidence: number | null;
  extraction_seq: number;
  normalized: OfferNormalizedV1 | null;
  normalisiert_at: string | null;
  normalisiert_by_name: string | null;
  manuell_ergaenzt: boolean;
  pruef_task_id: string | null;
  fehler_task_id: string | null;
  zugeordnet_at: string | null;
  verworfen_at: string | null;
  verworfen_grund: string | null;
  /** status in OFFER_BEARBEITBARE_STATUS and not verworfen. */
  bearbeitbar: boolean;
  created_at: string;
  updated_at: string;
}

/** One per submission of the placement (Slice-8 order) + one 'nicht_zugeordnet' group last. */
export interface OfferSubmissionGruppe {
  submission_id: string | null;
  submission_number: string | null;
  carrier_uid: string | null;
  name: string | null;
  logo_url: string | null;
  submission_status: SubmissionStatus | null;
  /** submission_status in OFFER_ZUORDENBARE_SUBMISSION_STATUS. */
  zuordenbar: boolean;
  aktuelle: OfferEintrag | null;
  /** Every version, received_at DESC. */
  items: OfferEintrag[];
}

export type OfferSperrgrund = 'placement_locked' | 'placement_status_invalid';

export interface OfferListResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  branchencode: string;
  /** closure offen AND status in OFFER_UPLOAD_ERLAUBTE_STATUS. */
  aktion_erlaubt: boolean;
  sperrgrund: OfferSperrgrund | null;
  zaehler: {
    gesamt: number;
    aktuell: number;
    zu_pruefen: number;
    nicht_zugeordnet: number;
    fehlgeschlagen: number;
  };
  /** fixture = demo data (the Slice-6 is_demo posture). */
  extractor: { driver: 'fixture' | 'phoeniqs'; is_demo: boolean };
  gruppen: OfferSubmissionGruppe[];
}

export interface OfferUploadResponse extends OfferListResponse {
  offer_document_id: string;
  /** always false on 201 (409 on hit). */
  duplicate: boolean;
}

export interface OfferZuordnenBody {
  submission_id: string;
}

export interface OfferNormalisiertBody {
  praemie_jahr?: number | null;
  praemie_netto?: number | null;
  praemie_brutto?: number | null;
  versicherungssumme?: number | null;
  selbstbehalt?: number | null;
  vertragsdauer_monate?: number | null;
  beginn?: string | null;
  gueltig_bis?: string | null;
  offert_nummer?: string | null;
  deckungen?: Array<{
    rule_key: string;
    status: OfferDeckungStatus;
    deckungssumme?: number | null;
    selbstbehalt?: number | null;
    hinweis?: string | null;
  }>;
  ausschluesse?: string[];
}

export interface OfferVerwerfenBody {
  /** <= 2000. */
  grund?: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- reserved body, symmetric with the other action bodies
export interface OfferAktuellBody {}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- reserved body, symmetric with the other action bodies
export interface OfferReExtractBody {}

// ── Pure counters ──────────────────────────────────────────────────────────────

/** The five list counters derived from the flat offer list (verworfen rows do not count). */
export function offerZaehler(items: readonly OfferEintrag[]): {
  gesamt: number;
  aktuell: number;
  zu_pruefen: number;
  nicht_zugeordnet: number;
  fehlgeschlagen: number;
} {
  const z = { gesamt: 0, aktuell: 0, zu_pruefen: 0, nicht_zugeordnet: 0, fehlgeschlagen: 0 };
  for (const it of items) {
    if (it.verworfen_at !== null) continue;
    z.gesamt += 1;
    if (it.is_current) z.aktuell += 1;
    if (it.idp_status === OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN) z.zu_pruefen += 1;
    if (it.submission_id === null) z.nicht_zugeordnet += 1;
    if (it.idp_status === OFFER_IDP_STATUS.FEHLGESCHLAGEN) z.fehlgeschlagen += 1;
  }
  return z;
}
